import { forwardRef } from 'react';
import type { ComponentPropsWithoutRef, CSSProperties, ReactNode } from 'react';

import type { ColorType } from '@lib/types';
import { cn, cva, type VariantProps, setThisClass } from '@lib/utils';

const progressRailVariants = cva(
  [
    // Layout
    'relative flex w-full overflow-hidden rounded-full',
    // RTL support
    '[--p-from:translateX(-100%)] [--p-to:translateX(0)]',
    'rtl:[--p-from:translateX(100%)]',
  ],
  {
    variants: {
      variant: {
        default: 'bg-surface-raised',
        soft: 'bg-this-bg',
      },
      size: {
        xs: 'h-1',
        sm: 'h-1.5',
        md: 'h-2',
        lg: 'h-3',
        xl: 'h-4',
      },
      showRail: {
        true: '',
        false: 'bg-transparent',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'md',
      showRail: true,
    },
  }
);

const progressBarVariants = cva(
  [
    // Layout & animations
    'relative rounded-full transition-[width] ease-out',
    // Background
    'bg-this-solid',
  ],
  {
    variants: {
      isIndeterminate: {
        true: [
          'absolute inset-0 origin-[100%_50%]',
          '[--p-from:translateX(-100%)_scaleX(.1)] [--p-to:translateX(100%)_scaleX(1)]',
          'rtl:origin-[50%_100%]',
          'rtl:[--p-from:translateX(100%)_scaleX(.1)] rtl:[--p-to:translateX(-100%)_scaleX(1)]',
          'animate-[progress-increase_1.5s_cubic-bezier(0.53,0.21,0.29,0.67)_infinite]',
        ],
        false: 'flex items-center justify-end leading-none',
      },
      isActive: {
        true: [
          'before:absolute before:inset-0 before:rounded-sm before:bg-white before:content-[""]',
          'before:animate-[progress-active_2s_cubic-bezier(0.55,0.2,0.3,0.67)_infinite]',
        ],
        false: '',
      },
    },
    defaultVariants: {
      isIndeterminate: false,
      isActive: false,
    },
  }
);

type ProgressProps = {
  children?: ReactNode;
  value?: number;
  showRail?: boolean;
  isActive?: boolean;
  isIndeterminate?: boolean;
  color?: ColorType;
  animationDuration?: CSSProperties['animationDuration'];
  classNames?: {
    root?: string;
    bar?: string;
  };
  rootProps?: ComponentPropsWithoutRef<'div'>;
} & VariantProps<typeof progressRailVariants> &
  ComponentPropsWithoutRef<'div'>;

const Progress = forwardRef<HTMLDivElement, ProgressProps>(
  (
    {
      children,
      value = 0,
      showRail = true,
      isActive = false,
      isIndeterminate = false,
      color = 'neutral',
      variant = 'default',
      size = 'md',
      animationDuration,
      className,
      classNames,
      style,
      rootProps,
      ...rest
    },
    ref
  ) => {
    return (
      <div
        {...rootProps}
        className={cn(
          progressRailVariants({ variant, size, showRail }),
          setThisClass(color),
          classNames?.root
        )}
      >
        <div
          ref={ref}
          {...rest}
          className={cn(
            progressBarVariants({ isIndeterminate, isActive }),
            className,
            classNames?.bar
          )}
          style={{
            width: isIndeterminate ? '100%' : `${value}%`,
            animationDuration,
            ...style,
          }}
        >
          {children}
        </div>
      </div>
    );
  }
);

Progress.displayName = 'Progress';

export { Progress };
export type { ProgressProps };
